import React from 'react';
import { StepComponentProps, StepDefinition } from './StepDefinition';
import { VStack, Text, Table, Center, Spinner, HStack, Box, Checkbox as CheckBoxChakra, Button } from '@chakra-ui/react';
import { LotForEditionDTO, LotInDocumentDTO, LotInListDTO, Permission, Res } from 'core/api';
import { z } from 'zod';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { BasicButton, SvgIcon } from 'core/ui-next';
import * as Images from 'pos/Images';
import { WarehouseFacade } from 'pos/facades';
import { handleServerErrors, Result, ServerError, SessionProvider } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { Message, Modal } from 'core/ui-next/modal';
import * as SalesStore from '../../SalesStore';
import * as UI from 'core/ui';
import { addPrecondition } from 'pos/common';
import { AcceptActionButton, BackActionButton, Content, Filler, Footer, Header, PhoneOnly, PosScreen, ThemeManager } from 'pos/ui';
import { zSpecialDateString } from 'core/infrastructure-next';
import { Close } from 'core/img';
import { BarcodeReader } from 'core/ui-next';
import { Validator } from 'core/infrastructure';
import { CompositionState } from './Composition';
import { useAutoAdvance } from './useStepAutoAdvance';
import { useCompositionSetters } from './useCompositionSetters';

const editLotStateSchema = z.object({
	Number: z.string(),
	PackingDate: zSpecialDateString(),
	ExpirationDate: zSpecialDateString(),
	Notes: z.string()
});

type EditLotState = z.infer<typeof editLotStateSchema>;

const StateToDTO = (lot: LotForEditionDTO, state: EditLotState): LotForEditionDTO => ({
	...lot,
	Number: state.Number,
	PackingDate: state.PackingDate ? state.PackingDate : null,
	ExpirationDate: state.ExpirationDate ? state.ExpirationDate : null,
	Notes: state.Notes,
});

type EditLotProps = {
	lot: LotForEditionDTO

	// Le paso el onSave porque al pasar de una pantalla a otra hay parpadeo y no puedo evitarlo en dos execute distintos
	onSave: (lot: LotForEditionDTO) => Promise<void>
} & Modal.Actions<void>;

const EditLotScreen = ({ lot, onSave, cancel, close }: EditLotProps) => {

	const [editLotInput] = React.useState<EditLotState>({
		Number: lot.Number,
		PackingDate: lot.PackingDate ? lot.PackingDate : null,
		ExpirationDate: lot.ExpirationDate ? lot.ExpirationDate : null,
		Notes: lot.Notes || ''
	});

	const { Form, Input, DateInput, TextArea } = useControlsFor<EditLotState>();
	const editLotFormRef = React.useRef<FormRef<EditLotState>>(null);

	const onSubmit = React.useCallback((async () => {
		const result = await editLotFormRef.current!.getValue();

		if (!result.ok)
			return Result.error();

		const editedLot: LotForEditionDTO = StateToDTO(lot, result.value);

		await WarehouseFacade.saveLot(editedLot);
		await onSave(editedLot);

		close();

		return Result.ok();

	}), [lot, onSave, close]);

	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	const validate = Validator.create(async (model: EditLotState) => {
		return await WarehouseFacade.validateLot(StateToDTO(lot, model));
	});

	return <PosScreen size='small' screenId={'GET_LOT'} useKeyPad={false}>
		<Header title={Res.Web.EDIT_LOTS_LOT_NUMBER_TITLE()} onClose={cancel} />
		<Content>
			<VStack width={'full'} gap={'6'} padding={'6'}>
				<VStack width={'full'} alignItems={'flex-start'} gap={'1'}>
					<Text fontSize={'lg'} fontWeight={'semibold'}>{Res.Web.EDIT_LOTS_LOT_NUMBER_TITLE()}</Text>
				</VStack>
				<Box width={'full'}>
					<Form ref={editLotFormRef} initialValue={editLotInput} schema={editLotStateSchema} validate={validate}>
						<Box width={'full'} display={'flex'} flexDirection={'column'} gap={'4'}>
							<Input linkTo={'Number'} label={Res.Web.EDIT_LOTS_LOT_NUMBER_LBL()} />
							<DateInput linkTo={'PackingDate'} type='date' label={Res.Web.EDIT_LOTS_CONTROL_PACKING_DATE_LBL()} />
							<DateInput linkTo={'ExpirationDate'} type='date' label={Res.Web.EDIT_LOTS_CONTROL_EXPIRATION_DATE_LBL()} />
							<TextArea linkTo={'Notes'} label={Res.Web.EDIT_LOTS_NOTES_LBL()} maxLength={1024} />
						</Box>
					</Form>
				</Box>
				<BasicButton type='success' text={Res.Common.OK_BTN()} onClick={onSubmit} marginTop='6' />
			</VStack>
		</Content>
		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
				<Filler />
				<AcceptActionButton action={onSubmit} />
			</PhoneOnly>
		</Footer>
	</PosScreen>;
};

const editLot = addPrecondition(Preconditions.ensure(Permission.Lot_CreateOrModify), async (lot: LotForEditionDTO, onSave: (lot: LotForEditionDTO) => Promise<void>) => {
	return await Modal.show(EditLotScreen, { lot, onSave });
});

const deleteLot = addPrecondition(Preconditions.ensure(Permission.Lot_Delete), async (lot: LotForEditionDTO) => {

	if (lot.Movements.length > 0) {
		await Message.show(Res.Msg.MSG_LOT_WITH_MOVEMENTS_CANNOT_BE_DELETED());
		return Result.error();
	}

	const result = await Message.confirm(Res.Msg.MSG_CONFIRM_LOT_DELETION(lot.Number));
	if (result.ok)
		await WarehouseFacade.removeLot(lot.Id);

	return result;
});

const LotTable = ({
	lots,
	selected,
	onSelect,
	onEdit,
	onDelete,
	loading
}: {
	lots: LotInListDTO[],
	selected: LotInDocumentDTO,
	onSelect: (lot: LotInListDTO) => void
	onEdit: (lot: LotInListDTO) => Promise<void>,
	onDelete: (lot: LotInListDTO) => Promise<void>,
	loading: boolean

}) => {
	const isSelected = (lot: LotInListDTO) => selected && lot.Id === selected.Id;

	return <VStack width={'full'} height={'auto'} gap={'4'} pos={'relative'}>
		<Box borderRadius={'md'} borderWidth={'1px'} borderColor={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.700}' }} width={'full'} height={'full'} shadow={'md'} pos={'absolute'}>
			<Table.ScrollArea width={'full'}>
				<Table.Root striped stickyHeader width={'full'}>
					<Table.Header>
						<Table.Row fontWeight={'semibold'} fontSize={'sm'}>
							<Table.ColumnHeader>{Res.Web.SET_LOT_NUMBER_CLM()}</Table.ColumnHeader>
							<Table.ColumnHeader>{Res.Web.LOTS_CONTROL_PACKING_DATE_LBL()}</Table.ColumnHeader>
							{ThemeManager.isLargeDevice() ? <Table.ColumnHeader>{Res.Web.LOTS_CONTROL_EXPIRATION_DATE_LBL()}</Table.ColumnHeader> : null}
							<Table.ColumnHeader textAlign={'end'}>{Res.Web.SET_LOT_STOCK_CLM()}</Table.ColumnHeader>
						</Table.Row>
					</Table.Header>
					<Table.Body>
						{loading ?
							<Table.Row h={'full'}>
								<Table.Cell colSpan={5}>
									<Center>
										<Spinner color={{ base: '{colors.yellow.500}', _dark: '{colors.yellow.500}' }} h={'12'} w={'12'} borderWidth={'medium'} />
									</Center>
								</Table.Cell>
							</Table.Row>
							: lots.map(lot => (
								<Table.Row key={lot.Id} onClick={() => onSelect(lot)} cursor={'pointer'} fontSize={'sm'}>
									<Table.Cell
										background={isSelected(lot) ? { base: '{colors.blue.200}', _dark: '{colors.blue.500}' } : undefined}
										height={'8'}
										maxW={'26'}
										textWrap={'pretty'}>
										{ThemeManager.isLargeDevice() ?
											lot.Number
											: <VStack alignItems={'flex-start'} gap={'1'}>
												<Text textWrap={'pretty'} width={'full'}>{lot.Number}</Text>
												{lot.ExpirationDate && <Text fontSize={'xs'}>{Res.Web.LOTS_CONTROL_EXPIRATION_DATE_REDUCED_LBL(UI.Format.asDate(lot.ExpirationDate))}</Text>}
											</VStack>
										}
									</Table.Cell>
									<Table.Cell background={isSelected(lot) ? { base: '{colors.blue.200}', _dark: '{colors.blue.500}' } : undefined} height={'8'}>{lot.PackingDate ? UI.Format.asDate(lot.PackingDate) : ' '}</Table.Cell>
									{ThemeManager.isLargeDevice() ?
										<Table.Cell
											background={isSelected(lot) ? { base: '{colors.blue.200}', _dark: '{colors.blue.500}' } : undefined}
											height={'8'}>
											{lot.ExpirationDate ? UI.Format.asDate(lot.ExpirationDate) : ' '}
										</Table.Cell>
										: null
									}
									<Table.Cell background={isSelected(lot) ? { base: '{colors.blue.200}', _dark: '{colors.blue.500}' } : undefined} textAlign={'end'} height={'8'} padding={isSelected(lot) ? '1' : '3'}>
										{isSelected(lot) ?
											<HStack gap={'2'} overflow={'hidden'}>
												<VStack
													gap={'0.5'}
													padding={'0.75'}
													width={'8'}
													height={'8'}
													alignItems={'center'}
													justifyContent={'center'}
													bg={{ base: 'red.700', _dark: 'red.400' }}
													borderRadius={'md'}
													color={'white'}
													onClick={() => onDelete(lot)}
													cursor={'pointer'}
												>
													<SvgIcon src={Images.TrashCan} w='4' h='4' />
												</VStack>
												<VStack
													gap={'0.5'}
													padding={'0.75'}
													width={'14'}
													height={'8'}
													alignItems={'center'}
													justifyContent={'center'}
													bg={'primary'}
													borderRadius={'md'}
													color={'white'}
													onClick={() => onEdit(lot)}
													cursor={'pointer'}
												>
													<SvgIcon src={Images.Edit} w='4' h='4' />
												</VStack>
											</HStack>
											: UI.Format.asQuantity(lot.Stock)
										}
									</Table.Cell>
								</Table.Row>
							))
						}
					</Table.Body>
				</Table.Root>
			</Table.ScrollArea>
		</Box>
	</VStack>;
};

const searchCriteriaSchema = z.object({
	lotSelection: z.object({
		criteria: z.string()
	})
});

type SearchCriteriaState = z.infer<typeof searchCriteriaSchema>;

async function validate(state: CompositionState): Promise<boolean> {

	if (state.lotSelection.mode === 'ignore')
		return true;

	if (state.lotSelection.selected.Id === 0) {
		await Message.show(Res.Msg.MSG_LOT_MUST_BE_SELECTED());
		return false;
	}

	return true;
}

function clean(state: CompositionState): CompositionState {
	return {
		...state,
		lotSelection: {
			criteria: '',
			searchedLots: [],
			selected: { Id: 0, Number: '' },
			mode: 'assign',
			showOnlyWithStock: false
		}
	};
}

export const Component = ({ composition, setComposition, status, requestNext }: StepComponentProps) => {

	const [loading, setLoading] = React.useState<boolean>(false);
	const { setLotSelection } = useCompositionSetters(setComposition);

	useAutoAdvance({
		status,
		isCompleted: composition.lotSelection.mode === 'ignore',
		onAutoAdvance: requestNext,
	});

	const { Form, Input } = useControlsFor<SearchCriteriaState>();
	const formRef = React.useRef<FormRef<SearchCriteriaState>>(null);

	const disposeReaderRef = React.useRef<() => void>(() => { });

	const refreshLots = async (criteria: string, previousLot: Nullable<LotInListDTO> = null) => {

		const selectedProductId = composition.selectedProduct?.Id ? composition.selectedProduct?.Id : 0;

		const lots = await doSearch(() => WarehouseFacade.searchLotByNumber(criteria, selectedProductId, SalesStore.getLocation().SaleCenterId));

		if (previousLot != null && !lots.find(x => x.Id === previousLot.Id))
			lots.push(previousLot);

		const selectedLot = previousLot ? lots.find(x => x.Id === previousLot.Id) || null : null;
		setLotSelection({
			selected: selectedLot ? { Id: selectedLot.Id, Number: selectedLot.Number } : { Id: 0, Number: '' },
			searchedLots: lots,
			criteria: criteria
		});
	};

	const doSearch = async (action: () => Promise<LotInListDTO[]>): Promise<LotInListDTO[]> => {
		const result = await handleServerErrors(action, {
			onServerError: async (error: ServerError) => { await Message.show(error.message); }
		});
		return result.ok ? result.value : [];
	};

	const onEditLot = async (lot: LotForEditionDTO) => {
		await editLot(lot, async lot => {
			const selectedProductId = composition.selectedProduct?.Id ? composition.selectedProduct?.Id : 0;
			const lots = await doSearch(() => WarehouseFacade.searchLotByNumber(lot.Number, selectedProductId, SalesStore.getLocation().SaleCenterId));
			const updatedPreviousLot = lots.find(x => x.Number === lot.Number);
			await refreshLots(composition.lotSelection.criteria, updatedPreviousLot);
		});
	};

	const onAdd = async () => {

		const selectedProductId = composition.selectedProduct?.Id ? composition.selectedProduct?.Id : 0;

		if (!composition.selectedProduct?.Id) {
			throw new Error('Product has not been selected');
		};

		await onEditLot(await WarehouseFacade.createLot(selectedProductId));
	};

	const onDelete = async (lot: LotInListDTO) => {
		const result = await deleteLot(await WarehouseFacade.editLot(lot.Id));

		if (result.ok)
			await refreshLots(composition.lotSelection.criteria);
	};

	const onEdit = async (lot: LotInListDTO) => {
		await onEditLot(await WarehouseFacade.editLot(lot.Id));
	};

	const onSearch = async () => {
		setLoading(true);
		const result = await formRef.current!.getValue();

		if (result.ok) {
			await refreshLots(result.value.lotSelection.criteria);
		}
		setLoading(false);
	};

	const onNoLot = async () => {
		setLotSelection({
			selected: { Id: 0, Number: '' },
			searchedLots: [],
			criteria: '',
			mode: 'ignore',
			showOnlyWithStock: false
		});
	};

	const onClear = async () => {
		formRef.current?.setValue('lotSelection.criteria', '');
		setLotSelection({
			selected: { Id: 0, Number: '' },
			searchedLots: [],
			criteria: ''
		});
	};

	const onSelectLot = (lot: LotInListDTO) => {
		setLotSelection({ selected: { Id: lot.Id, Number: lot.Number } });
	};

	const onBarcodeRead = React.useCallback(async (barcode: string) => {

		const selectedProduct = composition.selectedProduct;
		if (selectedProduct === null) {
			return;
		}

		const lots = await doSearch(() => WarehouseFacade.searchLotByBarcode(barcode, selectedProduct.Id));
		setLotSelection({ searchedLots: lots });
	}, [composition.selectedProduct, setLotSelection]);

	React.useEffect(() => {
		disposeReaderRef.current = BarcodeReader.start(onBarcodeRead);

		return () => {
			disposeReaderRef.current();
		};
	}, [onBarcodeRead]);

	const lots = composition.lotSelection.showOnlyWithStock ? composition.lotSelection.searchedLots.filter(x => x.Stock > 0) : composition.lotSelection.searchedLots || [];

	return <VStack width={'full'} height={'full'} gap={'6'}>
		<VStack width={'full'} gap={'4'} alignItems={'flex-start'}>
			<Text fontSize={'md'} fontWeight={'semibold'}>{Res.Web.SET_LOT_TITLE()}</Text>
		</VStack>
		<VStack width={'full'} height={'full'} alignItems={'flex-start'} gap={'6'}>
			<HStack width={'full'} gap={'4'} justifyContent={'space-between'} alignItems={'center'}>
				<Form ref={formRef} initialValue={composition} schema={searchCriteriaSchema}>
					<Input
						linkTo={'lotSelection.criteria'}
						placeholder={Res.Web.SET_LOT_SEARCH_PLACE_HOLDER()}
						startElement={<SvgIcon onClick={onSearch} src={Images.Search} w={'5'} h={'5'} cursor={'pointer'} />}
						borderRadius={'4xl'}
						endElement={<SvgIcon src={Close} onClick={onClear} w={'5'} h={'5'} cursor={'pointer'} />}
						onKeyDown={UI.whenEnterKey(onSearch)}
					/>
				</Form>
				{!SessionProvider.isHubClient() &&
					<Box height={'full'}>
						<Button
							fontSize={'xs'}
							fontWeight={'semibold'}
							paddingX={'2'}
							paddingY={'1'}
							onClick={onAdd}
							variant={'outline'}
							height={'full'}
							display={'flex'}
							gap={'1'}
						>
							<SvgIcon src={Images.Plus} w={'4'} h={'4'} />
							{Res.Common.NEW_BTN()}
						</Button>
					</Box>
				}
				{ThemeManager.isLargeDevice() &&
					<CheckBoxChakra.Root
						cursor={'pointer'}
						colorPalette={'blue'}
						size={'sm'}
						checked={composition.lotSelection.showOnlyWithStock}
						onCheckedChange={(e) => {
							const isChecked = !!e.checked;
							setLotSelection({ showOnlyWithStock: isChecked });
						}}
					>
						<CheckBoxChakra.HiddenInput />
						<CheckBoxChakra.Control />
						<CheckBoxChakra.Label fontSize={'xs'}>{Res.Web.SET_LOT_WITH_STOCK_CHK()}</CheckBoxChakra.Label>
					</CheckBoxChakra.Root>
				}
			</HStack>

			{ThemeManager.isPhone() &&
				<Box width={'full'} display={'flex'} justifyContent={'flex-end'}>
					<CheckBoxChakra.Root
						cursor={'pointer'}
						colorPalette={'blue'}
						size={'sm'}
						checked={composition.lotSelection.showOnlyWithStock}
						onCheckedChange={(e) => {
							const isChecked = !!e.checked;
							setLotSelection({ showOnlyWithStock: isChecked });
						}}
					>
						<CheckBoxChakra.HiddenInput />
						<CheckBoxChakra.Control />
						<CheckBoxChakra.Label fontSize={'xs'}>{Res.Web.SET_LOT_WITH_STOCK_CHK()}</CheckBoxChakra.Label>
					</CheckBoxChakra.Root>
				</Box>
			}

			<VStack width={'full'} height={'full'} alignItems={'flex-start'} gap={'3'}>
				<Box width={'full'} display={'flex'} justifyContent={'flex-end'}>
					<Button
						fontSize={'xs'}
						fontWeight={'semibold'}
						paddingX={'2'}
						paddingY={'1'}
						onClick={onNoLot}
						variant={'outline'}
						height={'6'}
					>
						{Res.Web.SET_LOT_NO_LOT_BTN()}
					</Button>
				</Box>
				<Box width={'full'} height={'full'} overflow={'auto'}>
					<LotTable lots={lots} selected={composition.lotSelection.selected} onSelect={onSelectLot} onEdit={onEdit} onDelete={onDelete} loading={loading} />
				</Box>
			</VStack>
		</VStack>
	</VStack>;
};

const step: StepDefinition = {
	id: 'Lot',
	label: Res.Web.WIZARD_COMPOSE_SELECT_LOTS_LBL(),
	canFinish: false,
	useKeyPad: false,
	validate,
	clean,
	Component
};

export default step;
